import React, { useState, useEffect } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import { Checkbox, Slider, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useFilterControl } from '../../store/Store'; // Adjust the import path as needed
import { BASE_URL } from '../../apis/constant'; // Adjust the import path as needed

const FilterDrawer = ({ open, onClose, onFilterChange }) => {
  const { appliedFilters, setFilters, resetFilters, hasAppliedFilters } = useFilterControl();
  
  const [priceRange, setPriceRange] = useState(appliedFilters.priceRange);
  const [selectedCategories, setSelectedCategories] = useState(appliedFilters.categories);
  const [allCategories, setAllCategories] = useState({ data: [], loading: false });


  // Sync with store when it changes
  useEffect(() => {
    setPriceRange(appliedFilters.priceRange);
    setSelectedCategories(appliedFilters.categories);
  }, [appliedFilters]);

  // Sync with store when drawer opens and fetch categories
  useEffect(() => {
    if (open) {
      setPriceRange(appliedFilters.priceRange);
      setSelectedCategories(appliedFilters.categories);
      fetchCategories();
    }
  }, [open, appliedFilters]);

  const fetchCategories = async () => {
    if (allCategories.data.length > 0) return; // Don't fetch if already loaded

    setAllCategories({ data: [], loading: true });
    try {
      const response = await axios.get(`${BASE_URL}/api/get_all_categories/`);
      const responseData = response.data.data;

      
      setAllCategories({
        data: responseData,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching categories:', error);
      setAllCategories({
        data: [],
        loading: false
      });
    }
  };

  const handleCategoryChange = (categoryId, checked) => {
    const newSelectedCategories = checked
      ? [...selectedCategories, categoryId]
      : selectedCategories.filter(id => id !== categoryId);

    setSelectedCategories(newSelectedCategories);
    const updatedFilters = {
      categories: newSelectedCategories,
      priceRange: priceRange
    };
    
    setFilters(updatedFilters);
    applyFilters(updatedFilters);
  };

  const handlePriceChange = (value) => {
    setPriceRange(value);
    const updatedFilters = {
      categories: selectedCategories,
      priceRange: value
    };
    
    setFilters(updatedFilters);
    applyFilters(updatedFilters);
  };

  const applyFilters = (filters) => {
    onFilterChange?.(filters);
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setPriceRange([0, 5000]);
    resetFilters();
    onFilterChange?.({
      categories: [],
      priceRange: [0, 5000]
    });
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': { 
          width: { xs: '260px', sm: 300 },
          boxSizing: 'border-box',
          bgcolor: '#f9fafb'
        }
      }}
    >
      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            width: 2px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
            margin: 8px 0;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background-color: #ccc;
            border-radius: 1px;
          }
        `}
      </style>
      
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #f0f0f0',
        bgcolor: '#fff'
      }}>
        <div style={{ fontWeight: '600', fontSize: '16px' }}>
          Filters
        </div>

        {hasAppliedFilters() && 
        <Button
          type="text"
          size="small"
          icon={<ReloadOutlined />}
          onClick={clearAllFilters}
        >
          Reset
        </Button>}

      </Box>

      {/* Content */}
      <Box 
        className="custom-scrollbar"
        sx={{ 
          flex: 1,
          overflowY: 'auto',
          p: 2
        }}
      >
        {/* Price Range Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2 
          }}>
            <span style={{ fontWeight: '600', fontSize: '14px' }}>
              Price Range
            </span>
            <div style={{
              color: '#666',
              fontSize: '12px',
              fontWeight: '500'
            }}>
              <span style={{ fontSize: '16px'}}>৳</span>{priceRange[0]} - <span style={{ fontSize: '16px'}}>৳</span>{priceRange[1]}
            </div>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '30px' }}>
            <Slider
              range
              step={50}
              min={0}
              max={5000}
              value={priceRange}
              onChange={handlePriceChange}
              style={{ width: '100%' }}
            />
          </Box>
        </Box>

        {/* Categories Section */}
        <Box>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            fontWeight: '600',
            fontSize: '14px'
          }}>
            <span>Categories</span>
          </div>

          <Box>
            {allCategories.loading ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                Loading categories...
              </div>
            ) : allCategories.data.length > 0 ? (
              allCategories.data.map(category => (
                <Box
                  key={category.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1,
                    px: 0.5,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: '#fafafa',
                    }
                  }}
                  onClick={() => handleCategoryChange(
                    category.id,
                    !selectedCategories.includes(category.id)
                  )}
                >
                  <Checkbox
                    checked={selectedCategories.includes(category.id)}
                    onChange={(e) => handleCategoryChange(category.id, e.target.checked)}
                  />
                  <span style={{
                    flex: 1,
                    marginLeft: '8px',
                    color: '#333',
                    fontSize: '13px'
                  }}>{category.name}</span>
                  <span style={{
                    color: '#999',
                    fontSize: '11px'
                  }}>{category.count}</span>
                </Box>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                No categories found
              </div>
            )}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default FilterDrawer;